import type { ArtifactAttachmentRef } from '@/integration/types.js';
import {
  isSharedWithWorkspace,
  workspaceArtifactContentUrl,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

/**
 * Turn a workspace file into the descriptor a message references it by. Attaching copies no bytes and
 * no storage key ever reaches the client — the server resolves the key from `artifact_id`.
 *
 * The revision is PINNED to the head as it stands right now, at the moment of attaching, and the url
 * points at that same revision. Chat history has to stay reproducible: editing the file later must not
 * change what an already-sent message referred to.
 */
export function artifactAttachmentRef(
  workspaceId: string,
  artifact: WorkspaceArtifact,
): ArtifactAttachmentRef {
  const revision = artifact.headRevision;
  return {
    artifact_id: artifact.id,
    revision,
    name: artifact.name,
    content_type: artifact.contentType,
    /** The registry reports an unknown size as `-1`; a negative number is not something to put on a
     *  wire the service validates, and the size here is a courtesy value it recomputes anyway. */
    size: artifact.size >= 0 ? artifact.size : 0,
    url: workspaceArtifactContentUrl(workspaceId, artifact.id, revision),
  };
}

/**
 * Which of these files are not open to the whole workspace, by artifact id. Travels beside the
 * references rather than inside them — a reference is the exact six-key shape the server reads, and this
 * fact is for the user, not the wire.
 *
 * The test is deliberately still "is it the whole workspace", not "can everyone in the target chat read
 * it". The chat these files land in is not known here — the user picks it afterwards — and a file shared
 * with SOME people may or may not cover the audience of whichever chat they choose. Widening this to a
 * per-chat answer means asking the server, and the warning it feeds is worth exactly one boolean.
 * A visibility this build has never heard of counts as narrow, fail-closed.
 */
export function restrictedArtifactIds(
  artifacts: WorkspaceArtifact[],
): string[] {
  return artifacts
    .filter((artifact) => !isSharedWithWorkspace(artifact))
    .map((artifact) => artifact.id);
}

/**
 * Whether a conversation has anyone else in it: `group` (its listed participants) and `public` (every
 * workspace member) are shared, `private` is not. An absent visibility reads as private — the wire field
 * is optional during the rolling-deploy window — and so does a conversation the caller has no row for.
 */
export function isSharedConversation(
  conversation: { visibility?: string | null } | null | undefined,
): boolean {
  return (
    conversation?.visibility === 'group' ||
    conversation?.visibility === 'public'
  );
}

/**
 * What the user is told when they attach a file that is not open to the whole workspace to a shared
 * conversation. Attaching is allowed — in a shared conversation all content is shared with everyone in
 * it — but the consequence is not obvious, so it is spelled out: the AGENT reads the file for whoever is
 * in the chat, on this turn and on every later one.
 *
 * The second half used to say flatly that other people cannot open the file. That was true while a file
 * was either private or open to everyone; with a chat audience and a picked list it is false for exactly
 * the people the file was shared with, who can open it perfectly well. It now says what is actually
 * guaranteed — the content route still enforces the file's own audience, so anyone OUTSIDE it gets a 404
 * — and leaves the people inside it alone.
 */
export function restrictedArtifactWarning(count: number): string {
  if (count === 1) {
    return (
      'This file is not shared with the whole workspace. Attaching it lets the agent read it for ' +
      'everyone in this chat, now and on every later turn. Anyone outside the file’s own audience ' +
      'still cannot open or download the file itself.'
    );
  }
  return (
    `${count} of these files are not shared with the whole workspace. Attaching them lets the agent ` +
    'read them for everyone in this chat, now and on every later turn. Anyone outside a file’s own ' +
    'audience still cannot open or download it.'
  );
}
